import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Index from "./pages/Index";
import { AppHeader } from "./components/AppHeader";
import { GiftBanner } from "./components/GiftBanner";
import { AppFooter } from "./components/AppFooter";
import { Footer } from "./components/Footer";
import { useTestNotifications } from "./hooks/useTestNotifications";
import { useAutoTestRunner } from "./hooks/useAutoTestRunner";
import { ScrollToTop } from "./components/ScrollToTop";
import { MandatoryRatingOverlay } from "./components/booking/MandatoryRatingOverlay";
import FloatingBackButton from "./components/FloatingBackButton";
import SessionLiveBanner from "./components/SessionLiveBanner";
import { GlobalNotificationPopup } from "./components/GlobalNotificationPopup";
import { CrawlerNoIndex } from "./components/CrawlerNoIndex";
import { useLocation } from "react-router-dom";

// Lazy-loaded pages for code splitting
const Auth = lazy(() => import("./pages/Auth"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Profile = lazy(() => import("./pages/Profile"));
const Browse = lazy(() => import("./pages/Browse"));
const BrowseAcademies = lazy(() => import("./pages/BrowseAcademies"));
const CreateExperience = lazy(() => import("./pages/CreateExperience"));
const ExperienceDetail = lazy(() => import("./pages/ExperienceDetail"));
const Bookings = lazy(() => import("./pages/Bookings"));
const Messages = lazy(() => import("./pages/Messages"));
const Chat = lazy(() => import("./pages/Chat"));
const CoachProfile = lazy(() => import("./pages/CoachProfile"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Checkout = lazy(() => import("./pages/Checkout"));
const BookingSuccess = lazy(() => import("./pages/BookingSuccess"));
const CoachPublicProfile = lazy(() => import("./pages/CoachPublicProfile"));
const SkillProgression = lazy(() => import("./pages/SkillProgression"));
const ExperienceCollaboration = lazy(() => import("./pages/ExperienceCollaboration"));
const CoachInvitations = lazy(() => import("./pages/CoachInvitations"));
const AcademyBuilderWizard = lazy(() => import("./pages/AcademyBuilderWizard"));
const ManageAcademy = lazy(() => import("./pages/ManageAcademy"));
const AcademyProfile = lazy(() => import("./pages/AcademyProfile"));
const AcademyJoin = lazy(() => import("./pages/AcademyJoin"));
const LearnerGroups = lazy(() => import("./pages/LearnerGroups"));
const CreateGroup = lazy(() => import("./pages/CreateGroup"));
const GroupDetail = lazy(() => import("./pages/GroupDetail"));
const AdminTestSetup = lazy(() => import("./pages/AdminTestSetup"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminInvites = lazy(() => import("./pages/AdminInvites"));
const InviteLanding = lazy(() => import("./pages/InviteLanding"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const BecomeCoach = lazy(() => import("./pages/BecomeCoach"));
const BecomeMaestro = lazy(() => import("./pages/BecomeMaestro"));
const BecomeVenuePartner = lazy(() => import("./pages/BecomeVenuePartner"));
const BecomeReferralPartner = lazy(() => import("./pages/BecomeReferralPartner"));
const AffiliateRegister = lazy(() => import("./pages/AffiliateRegister"));
const AffiliateDashboard = lazy(() => import("./pages/AffiliateDashboard"));
const HelpCenter = lazy(() => import("./pages/HelpCenter"));
const ContactUs = lazy(() => import("./pages/ContactUs"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const WaiverOfLiability = lazy(() => import("./pages/WaiverOfLiability"));
const HighRiskAddendum = lazy(() => import("./pages/HighRiskAddendum"));
const ProviderAgreement = lazy(() => import("./pages/ProviderAgreement"));
const CoachOnboarding = lazy(() => import("./pages/CoachOnboarding"));
const PartnerDashboard = lazy(() => import("./pages/PartnerDashboard"));
const MaestroCommandCenter = lazy(() => import("./pages/MaestroCommandCenter"));
const AcademyApply = lazy(() => import("./pages/AcademyApply"));
const CoachAcademy = lazy(() => import("./pages/CoachAcademy"));
const AllExperiences = lazy(() => import("./pages/AllExperiences"));
const BetaFeedback = lazy(() => import("./pages/BetaFeedback"));
const BetaReward = lazy(() => import("./pages/BetaReward"));
const BusinessCardProof = lazy(() => import("./pages/BusinessCardProof"));
const BusinessAcademyInfo = lazy(() => import("./pages/BusinessAcademyInfo"));
const CustomerAcademyInfo = lazy(() => import("./pages/CustomerAcademyInfo"));
const CoachesWanted = lazy(() => import("./pages/CoachesWanted"));
const MateoOpen = lazy(() => import("./pages/MateoOpen"));
const MateoOpenInfo = lazy(() => import("./pages/MateoOpenInfo"));
const MateoStage = lazy(() => import("./pages/MateoStage"));
const StageOnboarding = lazy(() => import("./pages/StageOnboarding"));
const SessionGuideDemo = lazy(() => import("./pages/SessionGuideDemo"));
const FoundryBuilder = lazy(() => import("./pages/FoundryBuilder"));
const OptOutAnalytics = lazy(() => import("./pages/OptOutAnalytics"));
const CoachPreviewShare = lazy(() => import("./pages/CoachPreviewShare"));
const Notifications = lazy(() => import("./pages/Notifications"));
const GiftExperience = lazy(() => import("./pages/GiftExperience"));
const GiftRedeem = lazy(() => import("./pages/GiftRedeem"));
const AdminReports = lazy(() => import("./pages/AdminReports"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const CreateAcademy = lazy(() => import("./pages/CreateAcademy"));

const queryClient = new QueryClient();

const NotificationManager = () => {
  useTestNotifications();
  useAutoTestRunner();
  return null;
};

const ConditionalHeader = () => {
  const location = useLocation();
  const pagesWithCustomHeaders = ['/dashboard', '/auth', '/'];
  
  if (pagesWithCustomHeaders.includes(location.pathname)) {
    return null;
  }
  
  return <AppHeader />;
};

const PageLoading = () => (
  <div className="flex items-center justify-center min-h-[40vh]">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <BrowserRouter>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <NotificationManager />
        <ScrollToTop />
        <CrawlerNoIndex />
        <MandatoryRatingOverlay />
        <SessionLiveBanner />
        <FloatingBackButton />
        <GlobalNotificationPopup />
        <div className="min-h-screen w-full pb-20 md:pb-0">
          <GiftBanner />
          <ConditionalHeader />
        <Suspense fallback={<PageLoading />}>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/browse" element={<Browse />} />
          <Route path="/browse/coaches" element={<Browse />} />
          <Route path="/browse-academies" element={<BrowseAcademies />} />
          <Route path="/all-experiences" element={<AllExperiences />} />
          <Route path="/experiences/:id" element={<ExperienceDetail />} />
          <Route path="/coach-edit" element={<CoachProfile />} />
          <Route path="/coach/:id" element={<CoachProfile />} />
          <Route path="/coach/public/:slug" element={<CoachPublicProfile />} />
          <Route path="/coach-academy" element={<CoachAcademy />} />
          <Route path="/academy/join" element={<AcademyJoin />} />
          <Route path="/academy/apply" element={<AcademyApply />} />
          <Route path="/academy/:slug" element={<AcademyProfile />} />
          <Route path="/create-experience" element={<CreateExperience />} />
          <Route path="/create-academy" element={<AcademyBuilderWizard />} />
          <Route path="/manage-academy/:id" element={<ManageAcademy />} />
          <Route path="/bookings" element={<Bookings />} />
          <Route path="/notifications" element={<Notifications />} />
          <Route path="/messages" element={<Messages />} />
          <Route path="/chat" element={<Chat />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/booking-success" element={<BookingSuccess />} />
          <Route path="/coach-invitations" element={<CoachInvitations />} />
          <Route path="/experiences/:experienceId/collaborate" element={<ExperienceCollaboration />} />
          <Route path="/learner-groups" element={<LearnerGroups />} />
          <Route path="/groups/:id" element={<GroupDetail />} />
          <Route path="/create-group" element={<CreateGroup />} />
          <Route path="/skill-progression" element={<SkillProgression />} />
          <Route path="/admin/test-setup" element={<AdminTestSetup />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="/admin/invites" element={<AdminInvites />} />
          <Route path="/admin/reports" element={<AdminReports />} />
          <Route path="/invite/:code" element={<InviteLanding />} />
          <Route path="/partner/dashboard" element={<PartnerDashboard />} />
          <Route path="/partner/business-academy" element={<BusinessAcademyInfo />} />
          <Route path="/partner/customer-academy" element={<CustomerAcademyInfo />} />
          <Route path="/maestro" element={<MaestroCommandCenter />} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/become-coach" element={<BecomeCoach />} />
          <Route path="/become-maestro" element={<BecomeMaestro />} />
          <Route path="/partner/venue" element={<BecomeVenuePartner />} />
          <Route path="/partner/referral" element={<BecomeReferralPartner />} />
          <Route path="/partner/affiliate" element={<AffiliateRegister />} />
          <Route path="/partner/affiliate/dashboard" element={<AffiliateDashboard />} />
          <Route path="/help" element={<HelpCenter />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/waiver" element={<WaiverOfLiability />} />
          <Route path="/high-risk-release" element={<HighRiskAddendum />} />
          <Route path="/provider-agreement" element={<ProviderAgreement />} />
          <Route path="/coach-onboarding" element={<CoachOnboarding />} />
          <Route path="/feedback" element={<BetaFeedback />} />
          <Route path="/beta-reward" element={<BetaReward />} />
          <Route path="/business-card" element={<BusinessCardProof />} />
          <Route path="/coaches-wanted" element={<CoachesWanted />} />
          <Route path="/mateo-open" element={<MateoOpenInfo />} />
          <Route path="/mateo-open/sessions" element={<MateoOpen />} />
          <Route path="/mateo-stage" element={<MateoStage />} />
          <Route path="/stage-onboarding" element={<StageOnboarding />} />
          <Route path="/demo/session-guide" element={<SessionGuideDemo />} />
          <Route path="/foundry/build" element={<FoundryBuilder />} />
          <Route path="/gift" element={<GiftExperience />} />
          <Route path="/gift/redeem" element={<GiftRedeem />} />
          <Route path="/coach-preview/:id" element={<CoachPreviewShare />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/opt-out-analytics" element={<OptOutAnalytics />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
        <Footer />
        <AppFooter />
        
        </div>
      </TooltipProvider>
    </BrowserRouter>
  </QueryClientProvider>
);

export default App;
